/**
 * MediaResultsGrid — таблица результатов медиа-прогона (ТЗ 2026-09-22 §8).
 *
 * Режим 1: строки с превью Ф_1..Ф_N (честные статусы: ok / no_variant_photo_used_group /
 * group_photo / limit_wait / not_found / ...). Режим 2: колонки Изображение_1..N.
 * Превью грузятся blob'ом через авторизованный axios-клиент (asset/file endpoint).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchMediaAssetBlob, type MediaResultRow } from '@/hooks/useMedia';

const STATUS_COLORS: Record<string, string> = {
  ok: 'bg-green-100 text-green-800',
  generated: 'bg-green-100 text-green-800',
  no_variant_photo_used_group: 'bg-amber-100 text-amber-800',
  group_photo: 'bg-amber-100 text-amber-800',
  group_photo_first_row_only: 'bg-amber-100 text-amber-800',
  no_variant_photo_other_variant: 'bg-amber-100 text-amber-800',
  limit_wait: 'bg-blue-100 text-blue-800',
  not_found: 'bg-red-100 text-red-700',
  generation_unsupported: 'bg-red-100 text-red-700',
};

export function MediaResultsGrid({
  rows,
  mode,
}: {
  rows: MediaResultRow[];
  mode: 'image_search' | 'image_generation';
}) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);
  if (rows.length === 0) {
    return (
      <div className="text-surface-400 text-center py-8 text-sm">
        {t('media.results.empty', { defaultValue: 'Для этого прогона результатов пока нет' })}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-xs text-surface-400">
        {mode === 'image_search' ? t('media.search_result_photo') : t('media.gen_result_photo')}
      </div>
      {rows.map((row) => {
        const statusKey = row.row_status ?? 'not_found';
        const colorClass = STATUS_COLORS[statusKey] ?? 'bg-surface-100 text-surface-600';
        return (
          <div
            className="flex items-start gap-3 p-3 rounded-lg border border-surface-100 bg-surface-50/50"
            key={row.row_index}
          >
            <span className="text-xs text-surface-400 w-6 shrink-0 pt-1">
              {row.row_index + 1}
            </span>
            <span className="text-xs text-surface-500 w-40 shrink-0 break-words pt-2">
              {row.raw_preview || '—'}
            </span>
            <span className="w-44 shrink-0 pt-2">
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${colorClass}`}
              >
                {t(`media.status.${statusKey}`, { defaultValue: statusKey })}
              </span>
            </span>
            <span className="flex flex-wrap gap-2">
              {row.photos.length === 0 ? (
                <span className="text-surface-300 text-xs pt-4">—</span>
              ) : (
                row.photos.map((p) => (
                  <GridThumb
                    key={p.asset_id + p.column}
                    assetId={p.asset_id}
                    alt={p.column}
                    onZoom={(u) => setLightbox({ url: u, alt: p.column })}
                  />
                ))
              )}
            </span>
          </div>
        );
      })}
      {lightbox ? <Lightbox info={lightbox} onClose={() => setLightbox(null)} /> : null}
    </div>
  );
}

function GridThumb({
  assetId,
  alt,
  onZoom,
}: {
  assetId: string;
  alt: string;
  onZoom: (url: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    let objUrl: string | null = null;
    setFailed(false);
    setUrl(null);
    fetchMediaAssetBlob(assetId)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        objUrl = u;
        setUrl(u);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [assetId]);

  if (failed) {
    return (
      <div
        className="h-16 w-16 shrink-0 rounded-md border border-surface-200 bg-surface-50 flex items-center justify-center text-surface-400 text-xs"
        title={alt}
      >
        ?
      </div>
    );
  }
  if (!url) {
    return (
      <div className="h-16 w-16 shrink-0 rounded-md border border-surface-100 bg-surface-50 animate-pulse" />
    );
  }
  return (
    <button
      type="button"
      onClick={() => onZoom(url)}
      title={alt}
      className="shrink-0 rounded-md overflow-hidden border border-surface-200 hover:ring-2 ring-primary-300 transition-shadow"
    >
      <img src={url} alt={alt} loading="lazy" className="h-16 w-16 object-cover" />
    </button>
  );
}

function Lightbox({ info, onClose }: { info: { url: string; alt: string }; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={info.alt}
      onClick={onClose}
    >
      <img src={info.url} alt={info.alt} className="max-h-full max-w-full rounded-lg shadow-2xl" />
      <button
        type="button"
        className="absolute top-4 right-4 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full"
        onClick={onClose}
      >
        ✕
      </button>
    </div>
  );
}
